/**
 * Opt-In API Service
 * API client for newsletter/notification double opt-in subscriptions
 */

import { apiClient } from './client';

/**
 * Available opt-in categories
 */
export type OptInCategory =
  | 'General'
  | 'Newsletter'
  | 'Marketing'
  | 'ProductUpdates'
  | 'SMS'
  | 'Beta'
  | 'EventNotifications';

/**
 * All available opt-in categories
 */
export const OPT_IN_CATEGORIES: OptInCategory[] = [
  'General',
  'Newsletter',
  'Marketing',
  'ProductUpdates',
  'SMS',
  'Beta',
  'EventNotifications',
];

/**
 * Display name map for opt-in categories (used in UI)
 */
export const OPT_IN_CATEGORY_DISPLAY_NAMES: Record<OptInCategory, string> = {
  General: 'optIn.categories.General',
  Newsletter: 'optIn.categories.Newsletter',
  Marketing: 'optIn.categories.Marketing',
  ProductUpdates: 'optIn.categories.ProductUpdates',
  SMS: 'optIn.categories.SMS',
  Beta: 'optIn.categories.Beta',
  EventNotifications: 'optIn.categories.EventNotifications',
};

/**
 * Request payload for creating an opt-in
 */
export interface CreateOptInRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  category: OptInCategory;
}

/**
 * Response from the opt-in endpoint
 */
export interface OptInResponse {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  category: OptInCategory;
  isVerified: boolean;
  isUnsubscribed: boolean;
  verifiedAt: string | null;
  unsubscribedAt: string | null;
  createdAt: string;
}

/**
 * Response from verify/unsubscribe endpoints (wraps OptInResponse)
 */
export interface OptInActionResponse {
  message: string;
  optIn: OptInResponse;
}

/**
 * Opt-in service API methods
 */
export const optInService = {
  /**
   * Create an opt-in for a single category
   * Sends verification email to the provided address
   */
  async createOptIn(email: string, category: OptInCategory, firstName?: string, lastName?: string): Promise<OptInResponse> {
    return apiClient.post<OptInResponse>('/api/optin', { email, firstName, lastName, category });
  },

  /**
   * Subscribe to a single category (authenticated)
   */
  async subscribe(category: OptInCategory): Promise<OptInResponse> {
    return apiClient.post<OptInResponse>('/api/optin/subscribe', { category });
  },

  /**
   * Subscribe to multiple categories at once (authenticated)
   */
  async subscribeBatch(categories: OptInCategory[]): Promise<OptInResponse[]> {
    return apiClient.post<OptInResponse[]>('/api/optin/subscribe/batch', { categories });
  },

  /**
   * Resend verification email for a category (authenticated)
   */
  async resendVerification(category: OptInCategory): Promise<void> {
    return apiClient.post<void>(`/api/optin/${category}/resend`);
  },

  /**
   * Get all opt-ins for the current user (authenticated)
   */
  async getMyOptIns(): Promise<OptInResponse[]> {
    return apiClient.get<OptInResponse[]>('/api/optin/my-opt-ins');
  },

  /**
   * Get opt-in status for a specific category (authenticated)
   */
  async getStatus(category: OptInCategory): Promise<OptInResponse> {
    return apiClient.get<OptInResponse>(`/api/optin/status/${category}`);
  },

  /**
   * Verify an opt-in token (public endpoint, called from email link)
   * Returns { message, optIn } on success, throws on error (400)
   */
  async verifyOptIn(token: string): Promise<OptInActionResponse> {
    return apiClient.get<OptInActionResponse>(`/api/optin/verify?token=${encodeURIComponent(token)}`);
  },

  /**
   * Unsubscribe via token (public endpoint, called from email link)
   * Returns { message, optIn } on success, throws on error (400)
   */
  async unsubscribeOptIn(token: string): Promise<OptInActionResponse> {
    return apiClient.get<OptInActionResponse>(`/api/optin/unsubscribe?token=${encodeURIComponent(token)}`);
  },

  /**
   * Subscribe to all available categories
   * Calls createOptIn for each category
   */
  async subscribeToAll(email: string): Promise<void> {
    const promises = OPT_IN_CATEGORIES.map((category) =>
      apiClient.post<OptInResponse>('/api/optin', { email, category })
    );
    await Promise.all(promises);
  },
};
